/* eslint-disable react/no-unescaped-entities */
/**
 * SmartSearchHero — Hero principal du nouveau parcours.
 * Décrivez votre situation → IA détecte la profession → résultats.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Sparkles, ArrowRight, ShieldCheck, Lock } from "lucide-react";

const ROTATING_EXAMPLES = [
  "Licenciement",
  "Création de société",
  "Succession",
  "Contrôle fiscal",
  "Bail commercial",
  "Divorce",
  "Achat immobilier",
];

const POPULAR = [
  "Licenciement",
  "Création de SAS",
  "Succession",
  "Contrôle fiscal",
  "Divorce",
  "Bail commercial",
];

export default function SmartSearchHero() {
  const nav = useNavigate();
  const [query, setQuery] = useState("");
  const [exIndex, setExIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setExIndex((i) => (i + 1) % ROTATING_EXAMPLES.length), 2400);
    return () => clearInterval(id);
  }, []);

  const placeholder = useMemo(
    () => `Décrivez votre situation (ex : ${ROTATING_EXAMPLES[exIndex]}...)`,
    [exIndex]
  );

  const submit = (val) => {
    const q = (val ?? query).trim();
    if (!q) return;
    setLoading(true);
    nav(`/recherche?q=${encodeURIComponent(q)}`);
  };

  return (
    <section className="relative overflow-hidden" data-testid="smart-search-hero">
      {/* Visuel d'arrière-plan : vert profond + halo subtil + image discrète */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0"
             style={{
               background:
                 "radial-gradient(ellipse 80% 60% at 20% 20%, rgba(46, 204, 113, 0.10), transparent 60%), " +
                 "radial-gradient(ellipse 70% 60% at 90% 90%, rgba(245, 196, 0, 0.10), transparent 60%), " +
                 "var(--cb-bg)",
             }}
        />
        <img
          src="https://images.unsplash.com/photo-1589994965851-a8f479c573a9?auto=format&fit=crop&w=1600&q=80"
          alt=""
          aria-hidden="true"
          className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-[0.08] pointer-events-none"
        />
        <div className="absolute right-10 bottom-10 w-64 h-64 rounded-full blur-3xl pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(245,196,0,0.16), transparent 70%)" }} />
      </div>

      <div className="max-w-4xl mx-auto px-5 lg:px-8 pt-20 lg:pt-28 pb-20 lg:pb-28 text-center">
        <div className="cb-fade-up">
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] cb-gold font-semibold mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            Plateforme premium des professions réglementées
          </span>

          <h1 className="cb-serif text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] font-semibold cb-text-ivory tracking-tight mb-6">
            Trouvez le <span style={{ color: "#F5C400" }}>spécialiste</span> adapté{" "}
            <br className="hidden sm:block" />
            à votre situation.
          </h1>

          <p className="text-base md:text-lg text-[#BFC8C2] max-w-xl mx-auto leading-relaxed mb-10">
            Décrivez votre situation. L'IA CONFILEX prépare votre dossier et sélectionne les 3 spécialistes les plus adaptés.
          </p>

          {/* Barre de recherche */}
          <div className="cb-search-glow max-w-2xl mx-auto">
            <form
              onSubmit={(e) => { e.preventDefault(); submit(); }}
              className="flex flex-col sm:flex-row items-stretch gap-2 bg-[#05291B]/80 backdrop-blur-xl border border-[#063D25] rounded-2xl sm:rounded-full p-2 shadow-[0_18px_50px_-20px_rgba(0,0,0,0.6)] focus-within:border-[#F5C400]/50 transition-colors"
              data-testid="hero-search-form"
            >
              <div className="flex items-center gap-3 px-4 flex-1">
                <Search className="w-5 h-5 cb-gold shrink-0" strokeWidth={1.8} />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={placeholder}
                  className="flex-1 bg-transparent border-none outline-none text-base md:text-lg cb-text-ivory placeholder:text-[#BFC8C2]/70 py-3 min-w-0"
                  data-testid="hero-search-input"
                  aria-label="Décrivez votre situation"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="cb-btn-gold inline-flex items-center justify-center gap-2 sm:px-7 sm:py-3.5 disabled:opacity-60"
                data-testid="hero-search-submit"
              >
                {loading ? "Recherche…" : "Commencer"}
                {!loading && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>

            {/* Suggestions populaires */}
            <div className="mt-6 flex flex-wrap gap-2 justify-center" data-testid="hero-popular-tags">
              {POPULAR.slice(0, 4).map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => { setQuery(tag); submit(tag); }}
                  className="text-xs px-3 py-1.5 rounded-full border border-[#063D25] cb-bg-surface text-[#F7F7F2]/85 hover:border-[#F5C400]/40 hover:text-[#F5C400] transition-colors"
                  data-testid={`hero-popular-${tag.toLowerCase().replace(/[^a-z]/g, "-")}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Barre de preuves — 3 items seulement, aérée */}
      <div className="border-y border-[#063D25] bg-[#0E4632]/90 backdrop-blur-sm" data-testid="hero-trust-bar">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-5 flex flex-wrap justify-center items-center gap-x-10 gap-y-3 text-sm text-[#F7F7F2]/85">
          {[
            { i: <ShieldCheck className="w-4 h-4 cb-gold" />, t: "Spécialistes vérifiés", id: "proof-experts" },
            { i: <Sparkles className="w-4 h-4 cb-gold" />, t: "Analyse par IA", id: "proof-ai" },
            { i: <Lock className="w-4 h-4 cb-gold" />, t: "Sécurisé & confidentiel", id: "proof-security" },
          ].map((row) => (
            <div key={row.t} className="flex items-center gap-2" data-testid={row.id}>
              {row.i}<span className="font-medium">{row.t}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
